import { useEffect, useState } from 'react';
import { api } from 'services/api';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { TPgRecurringRun } from 'services/api/paymentGateway/types';

const POLL_INTERVAL_MS = 2500;
const MAX_FAILURES = 5;

/**
 * Reads a recurring run and re-reads it while it is `running`. `runId: null` — nothing to read.
 */
export const useRecurringRun = (runId: string | null) => {
	// The state is keyed by the run it belongs to: a new `runId` shows nothing until it is loaded.
	const [state, setState] = useState<{
		runId: string | null;
		run: TPgRecurringRun | null;
		error: string | null;
	}>({ runId, run: null, error: null });
	const run = state.runId === runId ? state.run : null;
	const error = state.runId === runId ? state.error : null;

	useEffect(() => {
		if (!runId) return undefined;

		let cancelled = false;
		let timer = 0;
		let failures = 0;

		const load = async () => {
			try {
				const next = await api.paymentGateway.getRecurringRun(runId);
				if (cancelled) return;
				failures = 0;
				setState({ runId, run: next, error: null });
				if (next.status === 'running') timer = window.setTimeout(load, POLL_INTERVAL_MS);
			} catch (e) {
				if (cancelled) return;
				failures += 1;
				setState((prev) => ({
					run: prev.runId === runId ? prev.run : null,
					runId,
					error: getPgErrorMessage(e, 'Failed to load the run'),
				}));
				// A short network hiccup should not stop the progress view; a lasting error does.
				if (failures < MAX_FAILURES) timer = window.setTimeout(load, POLL_INTERVAL_MS * 2);
			}
		};

		void load();
		return () => {
			cancelled = true;
			window.clearTimeout(timer);
		};
	}, [runId]);

	return { run, error };
};
